import * as React from "react";
import {useEffect} from "react";
import {getListOfAllModules} from "../../../Utils/modules";
import {listModuleInstances, listProjects} from "../BackendFacade/apiCalls";
import {GlobalDictionary} from "../../../Utils/globalDictionary";
import {IUsageModuleProjectProps, IUsageModuleToProject} from "../Interfaces/ModulesUsage";
import {IModule} from "../../../BackendFacade/IModuleInstances";
import {IProject} from "../Interfaces/IAllProjects";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import {odxIconSets} from "../../../OdxComponents/constants";
import {simplifyForHtmlId} from "../../../Utils/transformer";
import OdxContentBox from "../../../OdxComponents/NativeOdx/Odx-Contentbox";

const LISTOFPROJECTS = "listOfProjects";

const ModulesUsage: React.FC = () => {
    const [usageModules, setUsageModules] = React.useState<IUsageModuleToProject[]>([]);
    const {createErrorToast, setLoading} = useMain();
    const baseId = 'hub-modules-usage';

    const listOfAllStaticModules = getListOfAllModules();

    const fetchData = React.useCallback(async () => {
        try {
            const allProjects = await getListOfAllProjects();
            if (allProjects.length === 0) {
                return;
            }
            await gatherAllUsedModules(allProjects);
        } catch (error) {
            createErrorToast("An error occurred while fetching data.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [createErrorToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getListOfAllProjects = async () => {
        const cachedProjects = GlobalDictionary.get(LISTOFPROJECTS);
        if (cachedProjects) {
            return cachedProjects as IProject[];
        }
        try {
            const data = await listProjects();
            if (data.success) {
                GlobalDictionary.set(LISTOFPROJECTS, data.data.value);
                return data.data.value as IProject[];
            } else {
                createErrorToast(data.message);
            }
        } catch (error) {
            createErrorToast("Failed to retrieve projects.");
            console.error(error);
        }
        return [] as IProject[];
    };

    const getProjectModules = async (projectId: string) => {
        try {
            const data = await listModuleInstances(projectId);
            if (data.success) {
                const ret = data.data.value as IModule[];
                ret.forEach((module) => {
                    module.projectId = projectId;
                });
                return ret;
            } else {
                createErrorToast(data.message);
            }
        } catch (error) {
            createErrorToast("Failed to retrieve project modules.");
            console.error(error);
        }
        return [];
    };

    const translateProjectIdToProjectName = (projectId: string) => {
        const projectList = GlobalDictionary.get(LISTOFPROJECTS) as IProject[];
        if (!projectList) return "";
        const project = projectList.find((i) => i.projectId === projectId);
        if (!project) return "";
        return project.displayName;
    };

    const gatherAllUsedModules = async (allProjects: IProject[]) => {
        let allModules: IModule[] = [];
        let listToLoad = [] as Promise<void>[];

        allProjects.forEach((project) => {
            listToLoad.push(
                getProjectModules(project.projectId).then((modules) => {
                    if (modules) {
                        allModules.push(...modules);
                    }
                })
            );
        });

        await Promise.all(listToLoad);

        const allStaticModules = getListOfAllModules();

        let usageTable: IUsageModuleToProject[] = [];
        allModules.forEach((module) => {
            let moduleInUsageTable = usageTable.find((i) => i.moduleName === module.staticModuleId);
            if (!moduleInUsageTable) {
                moduleInUsageTable = {
                    moduleName: module.staticModuleId,
                    projects: [{name: translateProjectIdToProjectName(module.projectId), count: 1}],
                    count: 1,
                };
                usageTable.push(moduleInUsageTable);
            } else {
                moduleInUsageTable.count++;
                const existingProject = moduleInUsageTable.projects.find((i) => i.name === translateProjectIdToProjectName(module.projectId));
                if (!existingProject) {
                    moduleInUsageTable.projects.push({
                        name: translateProjectIdToProjectName(module.projectId),
                        count: 1
                    });
                } else {
                    existingProject.count++;
                }
            }
        });

        Object.keys(allStaticModules).forEach((key) => {
            if (!usageTable.find((i) => i.moduleName === key)) {
                usageTable.push({
                    moduleName: key,
                    projects: [],
                    count: 0,
                });
            }
        });

        setUsageModules(usageTable);
    };

    const copyButtonContactPersons = (moduleId: string, projects: IUsageModuleProjectProps[]) => {
        const copyToClipboard = async () => {
            const filteredProjects = (await getListOfAllProjects()).filter((project) => projects.find((i) => i.name === project.displayName));
            const contactPersons = filteredProjects.map((project) => project.contacts).map((contacts) => contacts.map((contact) => contact.mail)).flat();
            // filter out duplicates
            const uniqueContactPersons = contactPersons.filter((value, index, self) => self.indexOf(value) === index);
            await navigator.clipboard.writeText(uniqueContactPersons.join(";"));
        }

        return (
            <odx-icon-button
                variant="ghost"
                onClick={copyToClipboard}
                id={`OdxButton_${baseId}-copy-contacts-${moduleId}`}
                icon={`${odxIconSets.core}::copy-content`}
            />
        );
    }

    return (
        <div id={`${baseId}-view`}>
            <OdxAreaHeader text={"Modules Usage"}
                           subTitle={"This page contains infos about all modules in DevSecOps Hub."}
                           odxSize={"large"}
                           id={`${baseId}-header`}
            />
            <OdxContentBox id={`${baseId}-content`}>
                <OdxTable className={"pt-10 table-fixed"}
                          variant={"striped"}
                          id={`${baseId}-table`}
                          headerData={
                              [
                                  {name: "Title", sortable: true, title: "Title"},
                                  {name: "Used in", title: "Used in"},
                                  {name: "No. of instances", title: "No. of instances", sortable: true},
                                  {name: "Copy contact persons", title: "Copy contact persons", required: true}
                              ]
                          }
                >
                    {usageModules.map((module, index) => {
                        const moduleInfo = listOfAllStaticModules[module.moduleName];
                        const moduleTitle = moduleInfo ? moduleInfo.moduleContent.title : "Unknown Module";
                        const moduleKey = simplifyForHtmlId(module.moduleName || `module-${index}`);
                        return (
                            <OdxTableRow key={module.moduleName ?? index} id={`${baseId}-row-${moduleKey}`}>
                                <OdxTableCell id={`${baseId}-cell-${moduleKey}-title`}>{moduleTitle}</OdxTableCell>
                                <OdxTableCell id={`${baseId}-cell-${moduleKey}-projects`}>
                                    {module.projects.map((i) => `${i.name} (${i.count})`).join(", ")}
                                </OdxTableCell>
                                <OdxTableCell id={`${baseId}-cell-${moduleKey}-instances`}>{module.count}</OdxTableCell>
                                <OdxTableCell id={`${baseId}-cell-${moduleKey}-copy`}>
                                    {copyButtonContactPersons(moduleKey, module.projects)}
                                </OdxTableCell>
                            </OdxTableRow>
                       );
                   })}
                </OdxTable>
            </OdxContentBox>
        </div>
    );
};

export default ModulesUsage;
